import { useState } from 'react';
import { FaPlus, FaTrashAlt, FaGripVertical, FaChevronRight, FaClock, FaVideo, FaFileAlt, FaQuestionCircle, FaTasks, FaLock, FaUnlock } from 'react-icons/fa';
import { FieldShell } from './FieldShell';
import { FieldSelect } from './FieldSelect';
import { useDomFieldName, useDomFieldId } from './_useFieldName';
import { onFormatTime } from './_videoMeta';

const BLOCK = 'LStudio__app_field';

/*
    🔥 TEMARIO DEL CURSO — secciones y clases

    La estructura de Udemy, Platzi o Domestika: módulos que contienen
    clases. Cada clase tiene tipo, duración y si es vista previa gratuita.

    ⚠️ POR QUÉ NO ES UN FieldRepeater ANIDADO

    Un repeater dentro de otro se puede armar, pero el reordenamiento
    se vuelve confuso: no queda claro si movés la clase dentro de su
    módulo o entre módulos. Acá cada nivel tiene sus propias flechas y
    la sección se puede plegar, que es lo que hace manejable un curso
    de ochenta clases.

    El valor es:
        [{ id, title, lessons: [{ id, title, type, duration, isPreview }] }]
*/

export const LESSON_TYPES = [
    { value: 'video', label: 'Video', icon: <FaVideo /> },
    { value: 'article', label: 'Lectura', icon: <FaFileAlt /> },
    { value: 'quiz', label: 'Cuestionario', icon: <FaQuestionCircle /> },
    { value: 'assignment', label: 'Ejercicio', icon: <FaTasks /> },
];

const onNewId = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

export const FieldCurriculum = ({
    id, label = 'Temario', hint, error,
    sections = [], onChange,
    isDisabled = false, span = 2,
}) => {

    const [useClosed, setClosed] = useState([]);

    const onToggle = (sectionId) => setClosed((prev) => (
        prev.includes(sectionId) ? prev.filter((s) => s !== sectionId) : [...prev, sectionId]
    ));

    // ---------------------------- 🔥 SECCIONES
    const onAddSection = () => onChange?.([
        ...sections,
        { id: onNewId(), title: '', lessons: [] },
    ]);

    const onSetSection = (index, key) => (event) => onChange?.(
        sections.map((s, i) => (i === index ? { ...s, [key]: event.target.value } : s)),
    );

    const onRemoveSection = (index) => onChange?.(sections.filter((_, i) => i !== index));

    const onMoveSection = (index, delta) => {
        const TARGET = index + delta;
        if (TARGET < 0 || TARGET >= sections.length) return;
        const NEXT = [...sections];
        [NEXT[index], NEXT[TARGET]] = [NEXT[TARGET], NEXT[index]];
        onChange?.(NEXT);
    };

    // ---------------------------- 🔥 CLASES
    const onAddLesson = (sIndex) => onChange?.(
        sections.map((s, i) => (i === sIndex
            ? { ...s, lessons: [...s.lessons, { id: onNewId(), title: '', type: 'video', duration: 0, isPreview: false }] }
            : s)),
    );

    const onSetLesson = (sIndex, lIndex, key, value) => onChange?.(
        sections.map((s, i) => (i === sIndex
            ? { ...s, lessons: s.lessons.map((l, j) => (j === lIndex ? { ...l, [key]: value } : l)) }
            : s)),
    );

    const onRemoveLesson = (sIndex, lIndex) => onChange?.(
        sections.map((s, i) => (i === sIndex
            ? { ...s, lessons: s.lessons.filter((_, j) => j !== lIndex) }
            : s)),
    );

    const onMoveLesson = (sIndex, lIndex, delta) => {
        const TARGET = lIndex + delta;
        const LESSONS = sections[sIndex].lessons;
        if (TARGET < 0 || TARGET >= LESSONS.length) return;
        const NEXT = [...LESSONS];
        [NEXT[lIndex], NEXT[TARGET]] = [NEXT[TARGET], NEXT[lIndex]];
        onChange?.(sections.map((s, i) => (i === sIndex ? { ...s, lessons: NEXT } : s)));
    };

    // ---------------------------- 🔥 TOTALES
    const TOTAL_LESSONS = sections.reduce((sum, s) => sum + s.lessons.length, 0);
    const TOTAL_TIME = sections.reduce(
        (sum, s) => sum + s.lessons.reduce((t, l) => t + (Number(l.duration) || 0), 0), 0,
    );

    const DOM_NAME = useDomFieldName();

    const DOM_ID = useDomFieldId(id);

    return (
        <FieldShell id={DOM_ID} label={label} error={error} span={span} isDisabled={isDisabled}
            hint={hint ?? `${sections.length} secciones · ${TOTAL_LESSONS} clases · ${onFormatTime(TOTAL_TIME)}`} >

            <div className={`${BLOCK}__curriculum`}>

                {
                    sections.length === 0 && (
                        <p className={`${BLOCK}__repeater_empty`}>El temario está vacío.</p>
                    )
                }

                {
                    sections.map((section, sIndex) => {

                        const IS_CLOSED = useClosed.includes(section.id);

                        const SECTION_TIME = section.lessons.reduce((t, l) => t + (Number(l.duration) || 0), 0);

                        return (
                            <div key={section.id} className={`${BLOCK}__cur_section`} data-closed={IS_CLOSED}>

                                <div className={`${BLOCK}__cur_head`}>

                                    <button type="button" className={`${BLOCK}__cur_caret`} data-open={!IS_CLOSED}
                                        aria-label={IS_CLOSED ? 'Expandir' : 'Contraer'}
                                        onClick={() => onToggle(section.id)} >
                                        <FaChevronRight />
                                    </button>

                                    <span className={`${BLOCK}__cur_num`}>{sIndex + 1}</span>

                                    <input name={DOM_NAME} autoComplete="nope" data-lpignore="true" data-1p-ignore="true" data-form-type="other" spellCheck={false} autoCorrect="off" autoCapitalize="off" type="text" className={`${BLOCK}__input ${BLOCK}__cur_title`}
                                        value={section.title} onChange={onSetSection(sIndex, 'title')}
                                        placeholder="Nombre de la sección" disabled={isDisabled}
                                        aria-label="Sección" />

                                    <span className={`${BLOCK}__cur_meta`}>
                                        {section.lessons.length} · {onFormatTime(SECTION_TIME)}
                                    </span>

                                    <span className={`${BLOCK}__cur_actions`}>
                                        <button type="button" aria-label="Subir sección"
                                            disabled={isDisabled || sIndex === 0}
                                            onClick={() => onMoveSection(sIndex, -1)} >↑</button>
                                        <button type="button" aria-label="Bajar sección"
                                            disabled={isDisabled || sIndex === sections.length - 1}
                                            onClick={() => onMoveSection(sIndex, 1)} >↓</button>
                                        <button type="button" data-danger="true" aria-label="Quitar sección"
                                            disabled={isDisabled}
                                            onClick={() => onRemoveSection(sIndex)} ><FaTrashAlt /></button>
                                    </span>

                                </div>

                                {
                                    !IS_CLOSED && (
                                        <div className={`${BLOCK}__cur_body`}>

                                            {
                                                section.lessons.map((lesson, lIndex) => (
                                                    <div key={lesson.id} className={`${BLOCK}__cur_lesson`}>

                                                        <span className={`${BLOCK}__cur_grip`} aria-hidden="true">
                                                            <FaGripVertical />
                                                        </span>

                                                        <div className={`${BLOCK}__cur_type`}>
                                                            <FieldSelect isBare
                                                                value={lesson.type}
                                                                options={LESSON_TYPES}
                                                                isDisabled={isDisabled}
                                                                isSearchable={false}
                                                                onChange={(e) => onSetLesson(sIndex, lIndex, 'type', e.target.value)}
                                                            />
                                                        </div>

                                                        <input name={DOM_NAME} autoComplete="nope" data-lpignore="true" data-1p-ignore="true" data-form-type="other" spellCheck={false} autoCorrect="off" autoCapitalize="off" type="text" className={`${BLOCK}__input`}
                                                            value={lesson.title} disabled={isDisabled}
                                                            onChange={(e) => onSetLesson(sIndex, lIndex, 'title', e.target.value)}
                                                            placeholder="Título de la clase" aria-label="Clase" />

                                                        <span className={`${BLOCK}__cur_duration`}>
                                                            <FaClock />
                                                            <input name={DOM_NAME} autoComplete="nope" data-lpignore="true" data-1p-ignore="true" data-form-type="other" spellCheck={false} autoCorrect="off" autoCapitalize="off" type="number" className={`${BLOCK}__input`}
                                                                value={Math.round((lesson.duration ?? 0) / 60)}
                                                                disabled={isDisabled} min={0}
                                                                onChange={(e) => onSetLesson(sIndex, lIndex, 'duration', Number(e.target.value) * 60)}
                                                                aria-label="Minutos" />
                                                            <span>min</span>
                                                        </span>

                                                        <button type="button" className={`${BLOCK}__cur_preview`}
                                                            data-on={lesson.isPreview} disabled={isDisabled}
                                                            title={lesson.isPreview ? 'Vista previa gratuita' : 'Solo para inscriptos'}
                                                            onClick={() => onSetLesson(sIndex, lIndex, 'isPreview', !lesson.isPreview)} >
                                                            {lesson.isPreview ? <FaUnlock /> : <FaLock />}
                                                        </button>

                                                        <span className={`${BLOCK}__cur_actions`}>
                                                            <button type="button" aria-label="Subir clase"
                                                                disabled={isDisabled || lIndex === 0}
                                                                onClick={() => onMoveLesson(sIndex, lIndex, -1)} >↑</button>
                                                            <button type="button" aria-label="Bajar clase"
                                                                disabled={isDisabled || lIndex === section.lessons.length - 1}
                                                                onClick={() => onMoveLesson(sIndex, lIndex, 1)} >↓</button>
                                                            <button type="button" data-danger="true" aria-label="Quitar clase"
                                                                disabled={isDisabled}
                                                                onClick={() => onRemoveLesson(sIndex, lIndex)} ><FaTrashAlt /></button>
                                                        </span>

                                                    </div>
                                                ))
                                            }

                                            <button type="button" className={`${BLOCK}__cur_add_lesson`}
                                                disabled={isDisabled} onClick={() => onAddLesson(sIndex)} >
                                                <FaPlus /> Agregar clase
                                            </button>

                                        </div>
                                    )
                                }

                            </div>
                        );
                    })
                }

                <button type="button" className={`${BLOCK}__repeater_add`}
                    disabled={isDisabled} onClick={onAddSection} >
                    <FaPlus /> Agregar sección
                </button>

            </div>

        </FieldShell>
    );
};
